<template>
  <div class="d-f fd-c ai-c w-100 g m-t">
    <!-- Invite code: required for label signup only -->
    <div v-if="type === 'label' && !inviteVerified" class="d-f fd-c ai-c w-100 g ai-s">
      <div class="tt-up p-ver ta-c">
        <p class="font-p2 mute">
          *Labels are invite only.
          <a
            class="text-link"
            href="https://form.typeform.com/to/QXo5QDjL"
            target="_blank"
            >Apply here</a
          >
        </p>
      </div>

      <div class="input-container">
        <label class="label" for="invite">Invite Code</label>
        <input
          type="text"
          id="invite"
          name="invite"
          v-model="invite"
          placeholder="Invite Code"
        />
      </div>
    </div>
    <div v-else class="d-f fd-c ai-c w-100 g ai-s">
      <div class="tt-up p-t ta-c d-f jc-c ai-c">
        <p class="font-p2 mute" style="max-width: 600px;">
          <!-- By clicking on any of the "Continue" buttons below, you agree to
          Algorhythm’s -->
          By clicking "Continue", you agree to Algorhythm’s
          <br />  
          <a class="text-link ws-pre" href="/legal/terms" target="_blank"
            >Terms of Use</a
          >
          and
          <a class="text-link ws-pre" href="/legal/privacy" target="_blank"
            >Privacy Policy</a
          >.
        </p>
      </div>

      <div class="d-f fd-c g p-t">
        <button
          type="button"
          class="as-str ta-c secondary d-f ai-c jc-c g"
          @click.prevent="onSocialLogin('google')"
          :disabled="loading"
        >
          <IconBase :h="15" :w="15" :filled="true">
            <IconGoogle />
          </IconBase>
          <span>Continue with Google</span>
        </button>

        <button
          type="button"
          class="as-str ta-c secondary d-f ai-c jc-c g"
          @click.prevent="onSocialLogin('apple')"
          :disabled="loading"
        >
          <IconBase :h="15" :w="15" :filled="true">
            <IconApple />
          </IconBase>
          <span>Continue with Apple</span>
        </button>
      </div>

      <div class="separator tt-up mute font-small">or with email</div>

      <!-- Email -->
      <div class="input-container w-100">
        <label class="label" for="email">Email address</label>
        <input
          type="email"
          id="email"
          name="email"
          v-model="email"
          autocomplete="email"
          placeholder="Email address"
        />
      </div>

      <!-- Password -->
      <div v-show="!!email.trim()" class="input-container w-100">
        <label class="label" for="password">Password</label>
        <input
          type="password"
          id="password"
          name="password"
          v-model="password"
          autocomplete="new-password"
          placeholder="Password"
        />
      </div>

      <!-- Confirm password -->
      <div v-show="!!email.trim()" class="input-container w-100">
        <label class="label" for="password2">Confirm Password</label>
        <input
          type="password"
          id="password2"
          name="password2"
          v-model="password2"
          autocomplete="new-password"
          placeholder="Confirm Password"
        />
      </div>
      <p
        v-show="password && password2 && password !== password2"
        class="as-s p-l red"
      >
        Passwords do not match
      </p>
    </div>

    <button
      type="submit"
      class="as-str ta-c"
      @click.prevent="onContinue"
      :disabled="loading || !enableRegisterBtn"
    >
      <span v-if="loading" class="loading-text">Loading</span>
      <span v-else>Continue</span>
    </button>
  </div>
</template>

<script>
import { auth, functions, rtdb } from "@/db.js";
import { mapActions } from "vuex";
import IconBase from "@/assets/icons/IconBase.vue";
import IconCancel from "@/assets/icons/IconCancel.vue";
import IconArrowRight from "@/assets/icons/IconArrowRight.vue";
import IconGoogle from "@/assets/icons/IconGoogle.vue";
import IconApple from "@/assets/icons/IconApple.vue";
import { oAuthMixin } from "@/mixins/oAuthMixin";

export default {
  name: "SignUp",
  mixins: [oAuthMixin],
  data() {
    return {
      email: "",
      password: "",
      password2: "",
      // INVITE CODE (disabled): To re-enable, uncomment below and set inviteVerified: false
      // invite: "",
      // inviteVerified: false,
      invite: "",
      inviteVerified: false, // for label: true after invite verified; artist skips invite step
    };
  },
  props: {
    type: {
      type: String,
    },
    forSubmitDemoModal: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    IconApple,
    IconGoogle,
    IconBase,
    IconCancel,
    IconArrowRight,
  },
  mounted() {},
  methods: {
    resetForm() {
      this.email = "";
      this.password = "";
      this.password2 = "";
      this.invite = "";
      this.inviteVerified = this.type !== "label";
    },
    // INVITE CODE (disabled): To re-enable, uncomment the method body and the invite step in template/onContinue
    validateInviteCode() {
      const input = this.invite.trim().toLowerCase();
      if (!input) return Promise.resolve(false);

      return rtdb
        .ref("invites")
        .child(input)
        .once("value")
        .then((snap) => {
          if (snap.exists()) {
            this.invite = input;
            return true;
          }
          return false;
        });
    },
    goBack() {
      if (this.forSubmitDemoModal) {
        this.$root.$emit("on-close");
      } else if (window.history.length > 1) {
        this.$router.go(-1); // Go back one step
      } else {
        this.$router.push("/"); // Navigate to the home page
      }
    },
    async onSocialLogin(providerType) {
      const inviteCode = this.type === "label" ? this.invite : "";
      if (providerType === "google") {
        await this.performGoogleSignup({
          userType: this.type,
          inviteCode,
          provider: "google.com",
        });
      } else if (providerType === "apple") {
        await this.performAppleSignup({
          userType: this.type,
          inviteCode,
          provider: "apple.com",
        });
      }
    },
    async onContinue() {
      if (this.type === "label" && !this.inviteVerified) {
        this.loading = true;
        this.validateInviteCode()
          .then((success) => {
            if (!success) {
              alert("This invite code does not exist");
            } else {
              this.inviteVerified = true;
            }
          })
          .finally(() => {
            this.loading = false;
          });
        return;
      }

      if (this.password && this.password !== this.password2) {
        alert("Passwords do not match");
        return;
      }

      this.handleEmailSignup({
        userType: this.type,
        inviteCode: this.type === "label" ? this.invite : "",
        provider: "email",
        email: this.email,
        password: this.password,
      });
    },
    async register() {
      switch (this.type) {
        case "artist":
          return this.registerArtist();
        case "label":
          return this.registerLabel();
      }
    },
    // INVITE CODE (disabled): To re-enable, uncomment the method body and the calls in registerArtist/registerLabel
    markInviteCodeRegistered(uid) {
      // return rtdb
      //   .ref("invites")
      //   .child(this.invite)
      //   .update({
      //     [uid]: "registered",
      //   })
      //   .then()
      //   .catch((err) => {
      //     alert(err.message);
      //   });
      return Promise.resolve();
    },
    registerArtist() {
      if (this.loading) return;
      this.loading = true;

      const usersRef = rtdb.ref().child("userSettings");

      Promise.resolve()
        .then(() => {
          return Promise.all([
            auth.createUserWithEmailAndPassword(this.email, this.password),
            usersRef.once("value").then((snap) => snap.numChildren() || 0),
          ]);
        })
        .then(([authData, countRef]) => {
          const settings = {
            member: countRef,
            // username: this.username,
            // usernameUrl: encodedUsername,
            locations: {
              loc0: "World",
            },
            genres: {
              gen0: "All Genres",
            },
            locationInfo: {
              placeRef: "X",
            },
          };

          return Promise.all([
            // Save user settings
            usersRef
              .update({
                [authData.user.uid]: settings,
              })
              .then(() => {
                this.setUserSettings(settings);
              }),

            // Artist: no invite to mark. Label email signup goes through signUpUser, not registerArtist.
          ]);
        })
        .then(() => {
          console.log("User created");
          // Set flag for onboarding in Vuex
          this.$store.commit("SET_NEW_USER_SIGNUP", true);
          sessionStorage.setItem("newUserSignUp", "true");
          if (this.forSubmitDemoModal) {
            this.$root.$emit("on-close", true);
          } else {
            // For new signups, always redirect to settings (onboarding)
            // After onboarding completes, user will be redirected to profile
            this.$router.push("/settings");
          }
        })
        .catch((err) => {
          alert(err.message);
          console.log(err.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    registerLabel() {
      if (this.loading) return;
      this.loading = true;

      Promise.resolve()
        .then(() => {
          // functions.useEmulator("127.0.0.1", 5001);
          const signUpLabel = functions.httpsCallable("signUpLabel");
          return signUpLabel({
            email: this.email,
            password: this.password,
            // labelName: this.labelName,
            // username,
          });
        })
        .then(async (response) => {
          const settings = response?.data?.settings;

          if (!settings) {
            throw new Error("Failed to create account");
          }

          const authData = await auth.signInWithEmailAndPassword(
            this.email,
            this.password
          );

          this.setLabelSettings(settings);

          // INVITE CODE (disabled): To re-enable, uncomment: return Promise.all([ this.markInviteCodeRegistered(authData.user.uid) ]);
          return Promise.all([]);
        })
        .then(() => {
          console.log("Label created");
          // Set flag for onboarding in Vuex
          this.$store.commit("SET_NEW_USER_SIGNUP", true);
          sessionStorage.setItem("newUserSignUp", "true");
          if (this.forSubmitDemoModal) {
            this.$root.$emit("on-close", true);
          } else {
            // For new signups, always redirect to settings (onboarding)
            // After onboarding completes, user will be redirected to profile
            this.$router.push("/settings");
          }
        })
        .catch((err) => {
          alert(err.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    ...mapActions({
      setUserSettings: "setUserSettings",
      setLabelSettings: "setLabelSettings",
    }),
  },
  computed: {
    enableRegisterBtn() {
      if (this.type === "label" && !this.inviteVerified) {
        return !!this.invite.trim();
      }
      return (
        this.email.trim() && this.password && this.password === this.password2
      );
    },
  },
  watch: {
    type() {
      // When switching account type (e.g. Artist → Label), reset so Label shows invite step
      this.resetForm();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

/* The dots are generated and animated here */
.loading-text::after {
  content: '';
  animation: typing-dots 2s steps(4, end) infinite;
}

@keyframes typing-dots {
  0%   { content: ''; }
  25%  { content: '.'; }
  50%  { content: '..'; }
  75%  { content: '...'; }
  100% { content: ''; }
}
</style>
